import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Users, Copy, LogOut as LeaveIcon, Trash2, Crown, BookOpen, Flame
} from "lucide-react";
import { API, useAuth, authFetch } from "@/App";

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await authFetch(`${API}/groups/${groupId}`);
      if (res.ok) setGroup(await res.json());
      else if (res.status === 403 || res.status === 404) {
        toast.error("Group not found");
        navigate("/groups");
      }
    } catch (err) { console.error("group detail load failed:", err); }
    setIsLoading(false);
  }, [groupId, navigate]);

  useEffect(() => { load(); }, [load]);

  const copyCode = () => {
    if (!group) return;
    navigator.clipboard?.writeText(group.invite_code).then(
      () => toast.success(`Code ${group.invite_code} copied`),
      () => toast.error("Copy failed")
    );
  };

  const leave = async () => {
    if (!confirm("Leave this group?")) return;
    const res = await authFetch(`${API}/groups/${groupId}/leave`, { method: "POST" });
    const d = await res.json().catch(() => ({}));
    if (res.ok) {
      toast.success(d.message || "Left group");
      navigate("/groups");
    } else {
      toast.error(d.detail || "Leave failed");
    }
  };

  const remove = async () => {
    if (!confirm("Permanently delete this group? This cannot be undone.")) return;
    const res = await authFetch(`${API}/groups/${groupId}`, { method: "DELETE" });
    if (res.ok) {
      toast.success("Group deleted");
      navigate("/groups");
    } else {
      const d = await res.json().catch(() => ({}));
      toast.error(d.detail || "Delete failed");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-islamic-blue-950 flex items-center justify-center">
        <div className="spinner w-12 h-12" />
      </div>
    );
  }

  if (!group) return null;
  const isOwner = group.owner_id === user?.user_id;

  return (
    <div data-testid="group-detail-page" className="min-h-screen bg-islamic-blue-950">
      <header className="glass border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/groups")}
            className="flex items-center gap-2 text-slate-400 hover:text-gold-400 transition-colors font-outfit"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-gold-400" />
            <h1 className="font-cinzel text-lg font-semibold text-slate-100 truncate max-w-[60vw]">
              {group.name}
            </h1>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <section className="glass-card rounded-2xl p-6 mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-slate-500 text-xs font-outfit uppercase tracking-wider">Invite Code</p>
            <button
              data-testid="invite-code-display"
              onClick={copyCode}
              className="font-mono text-2xl text-gold-400 tracking-widest mt-1 flex items-center gap-2 hover:opacity-80"
              title="Click to copy"
            >
              {group.invite_code}
              <Copy className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          <div className="flex gap-2">
            {isOwner ? (
              <Button
                data-testid="delete-group-btn"
                variant="ghost"
                onClick={remove}
                className="rounded-full border border-red-500/20 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            ) : (
              <Button
                data-testid="leave-group-btn"
                variant="ghost"
                onClick={leave}
                className="rounded-full border border-white/10 text-slate-300 hover:bg-white/5"
              >
                <LeaveIcon className="w-4 h-4 mr-1" /> Leave
              </Button>
            )}
          </div>
        </section>

        <h2 className="font-cinzel text-lg text-slate-100 mb-4">Members</h2>
        <div className="space-y-3">
          {(group.member_stats || []).map((m) => (
            <div
              key={m.user_id}
              data-testid={`member-row-${m.user_id}`}
              className="glass-card rounded-xl p-4 flex items-center gap-4"
            >
              <div className="font-cinzel text-gold-400/80 w-8 text-center">#{m.rank}</div>
              <div className="w-10 h-10 rounded-full bg-gold-400/10 flex items-center justify-center text-gold-400 font-cinzel">
                {m.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-outfit text-slate-100 truncate">
                    {m.name}
                    {m.user_id === user?.user_id && (
                      <span className="text-slate-500 text-xs ml-2">(you)</span>
                    )}
                  </p>
                  {m.user_id === group.owner_id && (
                    <Crown className="w-3.5 h-3.5 text-gold-400/80" />
                  )}
                </div>
                <div className="flex flex-wrap gap-3 mt-1 text-xs font-outfit text-slate-500">
                  <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {m.ayahs} ayahs</span>
                  <span>{m.surahs_completed} surahs</span>
                  <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {m.streak}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="font-cinzel text-gold-400 text-lg">{m.points.toLocaleString()}</p>
                <p className="text-slate-500 text-xs font-outfit">points</p>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
